/**
 * <EngagementGauge> — l'anneau d'engagement et son mot (molecule, L0.1).
 *
 * Un anneau `score`/100 (`<CircularProgress>`), le nombre au centre, et sous lui le mot du niveau
 * (« Excellent » / « Très bien » / « Bien » / « À améliorer »), à sa couleur. C'est la forme que
 * porte l'en-tête d'un rapport (`<LiveReportHeader>`), et la seule : la légende « Engagement » et le
 * « /100 » de la première jauge sont partis avec son dernier lecteur. Le tout se lit donc
 * « Engagement 93 sur 100 : Excellent » à un lecteur d'écran.
 *
 * Le nombre se règle sur le diamètre de l'anneau — `<CircularProgress>` le choisit —, et le mot le
 * suit : plus petit sous un petit anneau (`isLargeRing`).
 *
 * The level (label + color) is derived by the pure `engagementLevel` adapter, not inlined. Composes
 * the `<CircularProgress>` atom + `<Text>` — no hex, no business logic beyond the adapter call.
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"

import { CircularProgress, DEFAULT_RING_SIZE, isLargeRing } from "../../atoms/CircularProgress/CircularProgress"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { textInkOnFill } from "../../theme/tokens/colors"
import { engagementLevel } from "../../../ports/primary/ui/formatters/liveSessionReportFormatter"

export type EngagementGaugeProps = {
    /** Engagement score, 0–100. `null` → empty ring, "-" label, neutral level. */
    score: number | null
    /** Diamètre de l'anneau — celui de `<CircularProgress>` par défaut (80). */
    size?: number
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function EngagementGauge(props: EngagementGaugeProps) {
    const { score, size = DEFAULT_RING_SIZE, style, testID } = props
    const level = engagementLevel(score)
    const scoreLabel = score == null ? "-" : String(score)

    return (
        <View
            accessible
            accessibilityLabel={`Engagement ${scoreLabel} sur 100 : ${level.label}`}
            style={style}
            testID={testID}
        >
            <Stack gap="1" align="center">
                <CircularProgress
                    value={score ?? 0}
                    maxValue={100}
                    size={size}
                    strokeWidth={4}
                    color={level.color}
                    label={scoreLabel}
                />
                <Text size={isLargeRing(size) ? "text-sm" : "text-xs"} weight="bold" color={textInkOnFill(level.color)}>
                    {level.label}
                </Text>
            </Stack>
        </View>
    )
}
